import { useMemo } from 'react';
import type { EntityUpsertSessionValue } from './EntityUpsertSessionContext';

/** Inputs assembled by GenericEntityUpsertDialog for {@link useEntityUpsertSessionValue}. */
export type EntityUpsertSessionInput = EntityUpsertSessionValue;

/**
 * Memoized session object for {@link EntityUpsertSessionProvider}.
 * Keeps assembly out of the dialog while preserving the same dependency semantics.
 */
export function useEntityUpsertSessionValue(input: EntityUpsertSessionInput): EntityUpsertSessionValue {
    const {
        wrapWithFieldDebug,
        formData,
        existingEntity,
        entityType,
        entityId,
        isEditMode,
        isViewSurface,
        loading,
        schemaEntityBindings,
        schemaAttributes,
        nonIndexedHasLengthCountNamesSet,
        deferredArrayRefNamesSet,
        isEmptyForDeferLoad,
        deferredArrayPendingAdds,
        deferredArrayPendingRemoves,
        setPaginatedArrayRefViewer,
        setDeferredArrayAddField,
        onRequestAttributeValue,
        loadingAttribute,
        handleFieldChange,
        handleFieldChangeRef,
        isSetTransition,
        selectedActionWorkflow,
        actionWorkflowError,
        setActionWorkflowBrowserOpen,
        formKeyFromAttrName,
        handleGroupPreviewMembers,
        handleFilterPreview,
        attributeRefBrowserField,
        setAttributeRefBrowserField,
        loadingPossessiveScopeTier2,
        tier2Entitlement,
        referenceAttributesFromPossessiveScope,
        referenceAttributesFromTier2,
        possessiveScope,
        isPluginFunction,
        isRequestApprovalType,
        isPolicyRule,
        handleRequestorSetChange,
        handleTargetSetChange,
        targetSetFilterScope,
        relativeTargetAttributeError,
        setRelativeTargetAttributeError,
        policyRuleOnlySingleValueAttributesSelected,
        isContent,
        onContentLinkClick,
        isEmailTemplate,
        isFile,
        isGroup,
        originalData,
        setOriginalData,
        getReferenceDisplayName,
        updateReferenceEntity,
        clearReferenceEntity,
        entityService,
        multiValuedRawInputs,
        setMultiValuedRawInputs,
        emailTemplatesList,
        loadingEmailTemplates,
        allEntityTypes,
        entityTypesFromContext,
        disjointedEntityTypes,
        possessiveTargetAttributeKey,
        loadingPossessiveTargetAttributeTier2,
        tier2PossessiveTargetAttributeEntitlement,
        referenceAttributesFromPossessiveTargetAttribute,
        selectedReferenceAttributeInfo,
        actorsAttributeBrowserOpen,
        setActorsAttributeBrowserOpen,
        setPossessiveWipeConfirmPending,
        setPossessiveWipeConfirmOpen,
        attributesFromPossessiveTargetAttributeTier2,
        contentTypesCacheService,
        setError,
        targetEntitlement,
        decryptReveal,
        setDecryptReveal,
        revealedEncryptedFieldNames,
        setRevealedEncryptedFieldNames,
        encryptedFieldsClearOnSave,
        setEncryptedFieldsClearOnSave,
        encryptedCreateVisible,
        setEncryptedCreateVisible,
        encryptedEditStoredVisible,
        setEncryptedEditStoredVisible,
        encryptedViewDecryptedByName,
        setEncryptedViewDecryptedByName,
        encryptedViewPlainVisible,
        setEncryptedViewPlainVisible,
        revealedDecryptedCacheRef,
        multiRefBrowserField,
        setMultiRefBrowserField,
    } = input;

    return useMemo<EntityUpsertSessionValue>(
        () => ({
            wrapWithFieldDebug,
            formData,
            existingEntity,
            entityType,
            entityId,
            isEditMode,
            isViewSurface,
            loading,
            schemaEntityBindings,
            schemaAttributes,
            nonIndexedHasLengthCountNamesSet,
            deferredArrayRefNamesSet,
            isEmptyForDeferLoad,
            deferredArrayPendingAdds,
            deferredArrayPendingRemoves,
            setPaginatedArrayRefViewer,
            setDeferredArrayAddField,
            onRequestAttributeValue,
            loadingAttribute,
            handleFieldChange,
            handleFieldChangeRef,
            isSetTransition,
            selectedActionWorkflow,
            actionWorkflowError,
            setActionWorkflowBrowserOpen,
            formKeyFromAttrName,
            handleGroupPreviewMembers,
            handleFilterPreview,
            attributeRefBrowserField,
            setAttributeRefBrowserField,
            loadingPossessiveScopeTier2,
            tier2Entitlement,
            referenceAttributesFromPossessiveScope,
            referenceAttributesFromTier2,
            possessiveScope,
            isPluginFunction,
            isRequestApprovalType,
            isPolicyRule,
            handleRequestorSetChange,
            handleTargetSetChange,
            targetSetFilterScope,
            relativeTargetAttributeError,
            setRelativeTargetAttributeError,
            policyRuleOnlySingleValueAttributesSelected,
            isContent,
            onContentLinkClick,
            isEmailTemplate,
            isFile,
            isGroup,
            originalData,
            setOriginalData,
            getReferenceDisplayName,
            updateReferenceEntity,
            clearReferenceEntity,
            entityService,
            multiValuedRawInputs,
            setMultiValuedRawInputs,
            emailTemplatesList,
            loadingEmailTemplates,
            allEntityTypes,
            entityTypesFromContext,
            disjointedEntityTypes,
            possessiveTargetAttributeKey,
            loadingPossessiveTargetAttributeTier2,
            tier2PossessiveTargetAttributeEntitlement,
            referenceAttributesFromPossessiveTargetAttribute,
            selectedReferenceAttributeInfo,
            actorsAttributeBrowserOpen,
            setActorsAttributeBrowserOpen,
            setPossessiveWipeConfirmPending,
            setPossessiveWipeConfirmOpen,
            attributesFromPossessiveTargetAttributeTier2,
            contentTypesCacheService,
            setError,
            targetEntitlement,
            decryptReveal,
            setDecryptReveal,
            revealedEncryptedFieldNames,
            setRevealedEncryptedFieldNames,
            encryptedFieldsClearOnSave,
            setEncryptedFieldsClearOnSave,
            encryptedCreateVisible,
            setEncryptedCreateVisible,
            encryptedEditStoredVisible,
            setEncryptedEditStoredVisible,
            encryptedViewDecryptedByName,
            setEncryptedViewDecryptedByName,
            encryptedViewPlainVisible,
            setEncryptedViewPlainVisible,
            revealedDecryptedCacheRef,
            multiRefBrowserField,
            setMultiRefBrowserField,
        }),
        [
            wrapWithFieldDebug,
            formData,
            existingEntity,
            entityType,
            entityId,
            isEditMode,
            isViewSurface,
            loading,
            schemaEntityBindings,
            schemaAttributes,
            nonIndexedHasLengthCountNamesSet,
            deferredArrayRefNamesSet,
            isEmptyForDeferLoad,
            deferredArrayPendingAdds,
            deferredArrayPendingRemoves,
            setPaginatedArrayRefViewer,
            setDeferredArrayAddField,
            onRequestAttributeValue,
            loadingAttribute,
            handleFieldChange,
            handleFieldChangeRef,
            isSetTransition,
            selectedActionWorkflow,
            actionWorkflowError,
            setActionWorkflowBrowserOpen,
            formKeyFromAttrName,
            handleGroupPreviewMembers,
            handleFilterPreview,
            attributeRefBrowserField,
            setAttributeRefBrowserField,
            loadingPossessiveScopeTier2,
            tier2Entitlement,
            referenceAttributesFromPossessiveScope,
            referenceAttributesFromTier2,
            possessiveScope,
            isPluginFunction,
            isRequestApprovalType,
            isPolicyRule,
            handleRequestorSetChange,
            handleTargetSetChange,
            targetSetFilterScope,
            relativeTargetAttributeError,
            setRelativeTargetAttributeError,
            policyRuleOnlySingleValueAttributesSelected,
            isContent,
            onContentLinkClick,
            isEmailTemplate,
            isFile,
            isGroup,
            originalData,
            setOriginalData,
            getReferenceDisplayName,
            updateReferenceEntity,
            clearReferenceEntity,
            entityService,
            multiValuedRawInputs,
            setMultiValuedRawInputs,
            emailTemplatesList,
            loadingEmailTemplates,
            allEntityTypes,
            entityTypesFromContext,
            disjointedEntityTypes,
            possessiveTargetAttributeKey,
            loadingPossessiveTargetAttributeTier2,
            tier2PossessiveTargetAttributeEntitlement,
            referenceAttributesFromPossessiveTargetAttribute,
            selectedReferenceAttributeInfo,
            actorsAttributeBrowserOpen,
            setActorsAttributeBrowserOpen,
            setPossessiveWipeConfirmPending,
            setPossessiveWipeConfirmOpen,
            attributesFromPossessiveTargetAttributeTier2,
            contentTypesCacheService,
            setError,
            targetEntitlement,
            decryptReveal,
            setDecryptReveal,
            revealedEncryptedFieldNames,
            setRevealedEncryptedFieldNames,
            encryptedFieldsClearOnSave,
            setEncryptedFieldsClearOnSave,
            encryptedCreateVisible,
            setEncryptedCreateVisible,
            encryptedEditStoredVisible,
            setEncryptedEditStoredVisible,
            encryptedViewDecryptedByName,
            setEncryptedViewDecryptedByName,
            encryptedViewPlainVisible,
            setEncryptedViewPlainVisible,
            revealedDecryptedCacheRef,
            multiRefBrowserField,
            setMultiRefBrowserField,
        ]
    );
}
